import React, { useEffect, useRef, useState, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { SocketContext } from "../context/SocketContext";
import { useCall } from "./useCall";
import { jwtDecode } from "jwt-decode";
import { getUsers } from "../api";

export default function CallPage() {
  const socketRef = useContext(SocketContext);
  const { recipientMobile } = useParams();
  const { state } = useLocation();
  const callType = state?.callType || "video";
  const isCaller = state?.isCaller ?? true;
  const initialOffer = state?.initialOffer;
  const caller = state?.caller;
  const navigate = useNavigate();

  const token = localStorage.getItem("token");
  const myMobile = token ? jwtDecode(token).mobile : undefined;

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);

  const [status, setStatus] = useState("Connecting...");
  const [isMuted, setMuted] = useState(false);
  const [isCameraOff, setCameraOff] = useState(false);
  const [isSpeakerOn, setSpeakerOn] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [recipientName, setRecipientName] = useState("");
  const [recipientAvatar, setRecipientAvatar] = useState("");
  const [callerName, setCallerName] = useState("");
  const [callerAvatar, setCallerAvatar] = useState("");
  const [isCallAccepted, setIsCallAccepted] = useState(isCaller);

  const callHook = useCall({
    socketRef,
    myMobile,
    recipientMobile,
    callType,
    localVideoRef,
    remoteVideoRef,
    setStatus,
    isCaller,
    initialOffer,
    caller,
    isCallAccepted,
  });

  const { mute, unmute, toggleCamera, endCall } = isCallAccepted ? callHook : { mute: () => {}, unmute: () => {}, toggleCamera: () => {}, endCall: () => {} };

  // pass initialOffer and caller down to the call hook so callee can answer immediately

  useEffect(() => {
    // Fetch recipient details
    const fetchRecipientDetails = async () => {
      try {
        const response = await getUsers();
        const users = response.data.users;
        const recipient = users.find(user => user.mobile === recipientMobile);
        if (recipient) {
          setRecipientName(recipient.name);
          setRecipientAvatar(recipient.avatar);
        }
      } catch (error) {
        console.error("Failed to fetch recipient details:", error);
      }
    };

    // Fetch caller details if caller is provided
    const fetchCallerDetails = async () => {
      if (caller) {
        try {
          const response = await getUsers();
          const users = response.data.users;
          const callerUser = users.find(user => user.mobile === caller);
          if (callerUser) {
            setCallerName(callerUser.name);
            setCallerAvatar(callerUser.avatar);
          }
        } catch (error) {
          console.error("Failed to fetch caller details:", error);
        }
      }
    };

    if (recipientMobile) {
      fetchRecipientDetails();
    }
    fetchCallerDetails();
  }, [recipientMobile, caller]);

  useEffect(() => {
    // navigate back when call cancelled or failed
    if (status === "Call cancelled" || status === "Call failed") {
      navigate("/users");
    }
  }, [status, navigate]);

  const handleMute = () => {
    if (isMuted) {
      unmute();
      setMuted(false);
    } else {
      mute();
      setMuted(true);
    }
  };

  const handleToggleCamera = () => {
    toggleCamera();
    setCameraOff((prev) => !prev);
  };

  const handleEndCall = () => {
    endCall();
    navigate("/users");
  };

  const handleAccept = () => {
    setIsCallAccepted(true);
  };

  const handleDecline = () => {
    handleEndCall();
  };

  const enableAudio = async () => {
    if (remoteVideoRef.current) {
      try {
        // Resume audio context if suspended
        if (window.AudioContext || window.webkitAudioContext) {
          const AudioContext = window.AudioContext || window.webkitAudioContext;
          const audioContext = new AudioContext();
          if (audioContext.state === 'suspended') {
            await audioContext.resume();
            console.log("Audio: AudioContext resumed");
          }
        }

        // Force play the remote audio
        await remoteVideoRef.current.play();
        console.log("Audio: Manual play triggered");
        setAudioEnabled(true);
      } catch (e) {
        console.error("Audio: Failed to enable audio:", e);
      }
    }
  };

  // timer for call duration (starts when connected)
  useEffect(() => {
    let t = null;
    if (status === "Connected") {
      t = setInterval(() => setElapsed((s) => s + 1), 1000);
    } else {
      setElapsed(0);
    }
    return () => clearInterval(t);
  }, [status]);

  const formatElapsed = (s) => {
    const mm = String(Math.floor(s / 60)).padStart(2, "0");
    const ss = String(s % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  if (!isCallAccepted) {
    // Incoming call screen
    return (
      <div className="d-flex flex-column justify-content-center align-items-center text-center" style={{ height: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="d-flex flex-column align-items-center">
          {(callerAvatar || recipientAvatar) ? (
            <img
              src={callerAvatar || recipientAvatar}
              alt="Avatar"
              className="rounded-circle mb-4 shadow"
              style={{ width: 160, height: 160, objectFit: 'cover', border: '4px solid rgba(255,255,255,0.3)' }}
            />
          ) : (
            <div
              className="rounded-circle bg-white bg-opacity-25 text-white d-flex justify-content-center align-items-center mb-4 shadow"
              style={{ width: 160, height: 160, fontSize: 60, border: '4px solid rgba(255,255,255,0.3)' }}
            >
              {(callerName || recipientName || caller || recipientMobile || "?").charAt(0).toUpperCase()}
            </div>
          )}

          <h3 className="mb-2 text-white fw-bold" style={{ textShadow: '0 0 10px rgba(0,0,0,0.5)' }}>{callerName || recipientName || caller || recipientMobile}</h3>
          <div className="text-white mb-4" style={{ opacity: 0.8 }}>Incoming {callType} call...</div>
        </div>

        {/* Accept and Decline buttons */}
        <div className="d-flex align-items-center gap-4">
          <button
            className="btn bg-danger text-white rounded-circle p-3"
            onClick={handleDecline}
            title="Decline Call"
            style={{ width: 70, height: 70, border: 'none' }}
          >
            <i className="bi bi-telephone-fill fs-5"></i>
          </button>

          <button
            className="btn bg-success text-white rounded-circle p-3"
            onClick={handleAccept}
            title="Accept Call"
            style={{ width: 70, height: 70, border: 'none' }}
          >
            <i className="bi bi-telephone-fill fs-5"></i>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid" style={{ height: '100vh', backgroundColor: '#0a0a0a' }}>
        {callType === "video" ? (
          <>
            {/* WhatsApp-like video UI: full screen remote video, small local preview top-right, header top-left, controls bottom */}
            <div className="position-relative" style={{ height: '100vh', overflow: 'hidden' }}>
              {/* Remote video fills the entire screen */}
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                className="w-100 h-100"
                style={{ objectFit: 'cover' }}
              />

              {/* Local preview - small circular floating video top-right */}
              <video
                ref={localVideoRef}
                autoPlay
                muted
                playsInline
                className="rounded-circle border border-2 border-white"
                style={{ width: 100, height: 100, objectFit: 'cover', position: 'absolute', right: 20, top: 20, zIndex: 10 }}
              />

              {/* Header overlay - name and status top-left */}
              <div style={{ position: 'absolute', left: 20, top: 20, zIndex: 10, color: '#fff' }}>
                <div className="fw-bold" style={{ fontSize: '1.2rem', textShadow: '0 0 5px rgba(0,0,0,0.5)' }}>{callerName || recipientName || caller || recipientMobile}</div>
                <div className="small" style={{ opacity: 0.8, textShadow: '0 0 5px rgba(0,0,0,0.5)' }}>{status}{status === 'Connected' ? ` • ${formatElapsed(elapsed)}` : ''}</div>
              </div>

              {/* Controls overlay bottom center */}
              <div style={{ position: 'absolute', bottom: 40, left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}>
                <div className="d-flex align-items-center gap-4">
                  <button
                    className={`btn rounded-circle p-3 ${isMuted ? 'bg-danger text-white' : 'bg-white bg-opacity-25 text-white'}`}
                    onClick={handleMute}
                    title={isMuted ? 'Unmute' : 'Mute'}
                    style={{ width: 60, height: 60, border: 'none' }}
                  >
                    <i className={`bi ${isMuted ? 'bi-mic-mute-fill' : 'bi-mic-fill'} fs-5`}></i>
                  </button>

                  <button
                    className="btn bg-danger text-white rounded-circle p-3"
                    onClick={handleEndCall}
                    title="End Call"
                    style={{ width: 70, height: 70, border: 'none' }}
                  >
                    <i className="bi bi-telephone-fill fs-5"></i>
                  </button>

                  <button
                    className={`btn rounded-circle p-3 ${isCameraOff ? 'bg-danger text-white' : 'bg-white bg-opacity-25 text-white'}`}
                    onClick={handleToggleCamera}
                    title={isCameraOff ? 'Turn Camera On' : 'Turn Camera Off'}
                    style={{ width: 60, height: 60, border: 'none' }}
                  >
                    <i className={`bi ${isCameraOff ? 'bi-camera-video-off-fill' : 'bi-camera-video-fill'} fs-5`}></i>
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          // Audio call - WhatsApp-like UI: dark background, centered content, controls at bottom
          <div className="d-flex flex-column justify-content-center align-items-center text-center" style={{ height: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            {/* Center content */}
            <div className="d-flex flex-column align-items-center">
              {(callerAvatar || recipientAvatar) ? (
                <img
                  src={callerAvatar || recipientAvatar}
                  alt="Avatar"
                  className="rounded-circle mb-4 shadow"
                  style={{ width: 160, height: 160, objectFit: 'cover', border: '4px solid rgba(255,255,255,0.3)' }}
                />
              ) : (
                <div
                  className="rounded-circle bg-white bg-opacity-25 text-white d-flex justify-content-center align-items-center mb-4 shadow"
                  style={{ width: 160, height: 160, fontSize: 60, border: '4px solid rgba(255,255,255,0.3)' }}
                >
                  {(callerName || recipientName || caller || recipientMobile || "?").charAt(0).toUpperCase()}
                </div>
              )}

              {/* Attach audio elements */}
              <audio ref={localVideoRef} autoPlay muted style={{ display: "none" }} />
              <audio ref={remoteVideoRef} autoPlay volume={1} style={{ display: "none" }} onLoadedData={() => { console.log("Audio: Remote audio loaded"); remoteVideoRef.current.volume = 1; }} onError={(e) => console.error("Audio: Remote audio error", e)} onCanPlay={() => { console.log("Audio: Remote audio can play"); remoteVideoRef.current.volume = 1; }} onPlay={() => { console.log("Audio: Remote audio started playing, volume:", remoteVideoRef.current.volume, "muted:", remoteVideoRef.current.muted); }} onPause={() => console.log("Audio: Remote audio paused")} onVolumeChange={() => console.log("Audio: Volume changed to", remoteVideoRef.current.volume)} />

              <h3 className="mb-2 text-white fw-bold" style={{ textShadow: '0 0 10px rgba(0,0,0,0.5)' }}>{callerName || recipientName || caller || recipientMobile}</h3>
              <div className="text-white mb-3" style={{ opacity: 0.8 }}>{status}</div>

              {/* Timer */}
              {status === 'Connected' && (
                <div className="mb-4">
                  <span className="text-white fw-bold" style={{ fontSize: '1.1rem' }}>{formatElapsed(elapsed)}</span>
                </div>
              )}
            </div>

            {/* Bottom controls */}
            <div className="pb-5">
              <div className="d-flex align-items-center gap-4">
                {!audioEnabled && status === 'Connected' && (
                  <button
                    className="btn bg-success text-white rounded-circle p-3"
                    onClick={enableAudio}
                    title="Enable Audio"
                    style={{ width: 60, height: 60, border: 'none' }}
                  >
                    <i className="bi bi-volume-up-fill fs-5"></i>
                  </button>
                )}

                <button
                  className={`btn rounded-circle p-3 ${isMuted ? 'bg-danger text-white' : 'bg-white bg-opacity-25 text-white'}`}
                  onClick={handleMute}
                  title={isMuted ? 'Unmute' : 'Mute'}
                  style={{ width: 60, height: 60, border: 'none' }}
                >
                  <i className={`bi ${isMuted ? 'bi-mic-mute-fill' : 'bi-mic-fill'} fs-5`}></i>
                </button>

                <button
                  className="btn bg-danger text-white rounded-circle p-3"
                  onClick={handleEndCall}
                  title="End Call"
                  style={{ width: 70, height: 70, border: 'none' }}
                >
                  <i className="bi bi-telephone-fill fs-5"></i>
                </button>

                <button
                  className={`btn rounded-circle p-3 ${isSpeakerOn ? 'bg-white bg-opacity-50 text-white' : 'bg-white bg-opacity-25 text-white'}`}
                  onClick={() => setSpeakerOn((s) => !s)}
                  title="Speaker"
                  style={{ width: 60, height: 60, border: 'none' }}
                >
                  <i className={`bi ${isSpeakerOn ? 'bi-volume-up-fill' : 'bi-speaker'} fs-5`}></i>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
  );
}
